import { ArrowLeftOutlined } from '@ant-design/icons';
import { Button, Col, Flex, Row } from 'antd';
import AIProgressBar from 'components/Message/CreateMessageComponent/StepMessage/AiCreation/AIProgressBar/AIProgressBar';
import AiSuggestPrompts from 'components/Message/CreateMessageComponent/StepMessage/AiCreation/AiSuggestPrompts/AiSuggestPrompts';
import { useMessageService } from 'components/Message/useMessageService';
import SpeechAntdTextArea, { SpeechAntdTextAreaHandle } from 'components/UiKit/SpeechAntdTextArea/SpeechAntdTextArea';
import { useEffect, useRef, useState } from 'react';
import './AiCreationClosing.css';

type AllProps = {
  tones: string[];
  onBack: () => void;
  onSave: (closing?: string, otherInfo?: string, shouldExit?: boolean) => void;
};

function AiCreationClosing({ tones, onBack, onSave }: AllProps) {
  const closingRef = useRef<SpeechAntdTextAreaHandle>(null);
  const otherRef = useRef<SpeechAntdTextAreaHandle>(null);
  const { message, updateMessageAiFields } = useMessageService();
  const [closing, setClosing] = useState<string>('');
  const [otherInfo, setOtherInfo] = useState<string>('');

  const handleSubmit = async (shouldExit = false) => {
    const finalClosing = (await closingRef.current?.flush()) ?? closing;
    setClosing(finalClosing);

    const finalOther = (await otherRef.current?.flush()) ?? otherInfo;
    setOtherInfo(finalOther);

    await updateMessageAiFields({ aiClosing: finalClosing, aiOtherInfo: finalOther });
    onSave(finalClosing, finalOther, shouldExit);
  };

  useEffect(() => {
    setClosing(message?.aiClosing || '');
    setOtherInfo(message?.aiOtherInfo || '');
  }, [message?.aiClosing, message?.aiOtherInfo]);

  if (!message) return null;

  return (
    <div className="ai-creation-closing create-message-component-full-height">
      <div>
        <AIProgressBar currentStep={4} />
        <div className="create-message-component-title">How should this message close?</div>
        <div className="create-message-component-subtitle">
          Share the final thought, blessing, or encouragement you want to leave.
        </div>

        <AiSuggestPrompts
          type={message.type}
          tones={tones}
          recipientName={message.contacts?.[0]?.name}
          recipientRelationship={message?.contacts?.[0]?.relationship}
          eventType={message.eventType}
          slot="How should this message close?"
        />

        <SpeechAntdTextArea
          ref={closingRef}
          autoFocus
          value={closing}
          onChange={(value) => {
            setClosing(value);
          }}
          placeholder="Leave them with hope, Close with 'I'm proud of you—always,' Invite them to celebrate life, not mourn."
        />

        <div className="other-info-field">
          <p className="field-label">
            <label>Anything else to include? (optional)</label>
          </p>

          <SpeechAntdTextArea
            ref={otherRef}
            value={otherInfo}
            onChange={(value) => {
              setOtherInfo(value);
            }}
            placeholder="Anything else to include? (optional)"
            rows={4}
            maxLength={2000}
          />
        </div>
      </div>

      <Row className="create-message-component-footer">
        <Col span={12}>
          <Button onClick={onBack} type="link">
            <ArrowLeftOutlined /> Back
          </Button>
        </Col>
        <Col span={12} className="text-right">
          <Flex gap={20} justify="flex-end">
            <Button onClick={() => handleSubmit(true)}>Save & Exit</Button>
            <Button type="primary" onClick={() => handleSubmit()}>
              Next
            </Button>
          </Flex>
        </Col>
      </Row>
    </div>
  );
}

export default AiCreationClosing;
